import { authStore, setAuthStore } from '~/stores/auth'
import { setCompanyStore } from '~/stores/company'
import type { OAuthProviderType } from '~/types/OAuth'
import api from './api'

export interface LoginPayloadType {
  email: string
  password: string
}

export interface LoginResponseType {
  token: string
  user: Record<string, unknown>
  companies: Array<{
    company: { id: string; name: string; slug: string; domain?: string; logo?: string }
    role: string
    isDefault: boolean
  }>
  defaultCompanyId: string | null
}

export interface SelectCompanyPayloadType {
  companyId: string
}

function applyLoginResult(result: LoginResponseType) {
  setAuthStore('token', result.token)
  setAuthStore('user', result.user)
  setAuthStore('companies', result.companies)
  setAuthStore('companyId', result.defaultCompanyId)

  if (result.defaultCompanyId) {
    const company = result.companies.find(c => c.company.id === result.defaultCompanyId)?.company
    if (company) {
      setCompanyStore({
        id: company.id,
        name: company.name,
        slug: company.slug,
        logo: company.logo ?? null,
        domain: company.domain ?? null
      })
    }
  } else {
    // Sin empresa asignada: el store persistido de una sesión anterior no
    // debe sobrevivir, o el panel creería que sigue habiendo empresa activa.
    setCompanyStore({ id: null, name: null, slug: null, logo: null, domain: null })
  }
}

export const authService = {
  /** Config pública de OAuth para el login: qué proveedores están activos. */
  async getOAuthConfig() {
    const { data } = await api.get('/auth/oauth-config')
    return data.data as { providers: OAuthProviderType[] }
  },

  async login(payload: LoginPayloadType) {
    const { data } = await api.post('/auth/login', payload)
    const result = data.data as LoginResponseType
    applyLoginResult(result)
    return result
  },

  async googleLogin(accessToken: string) {
    const { data } = await api.post('/auth/google', { accessToken })
    const result = data.data as LoginResponseType
    applyLoginResult(result)
    return result
  },

  async logout() {
    try {
      await api.post('/auth/logout')
    } catch {}
    setAuthStore('token', null)
    setAuthStore('user', null)
    setAuthStore('companyId', null)
    setAuthStore('companies', [])
    setCompanyStore({ id: null, name: null, slug: null, logo: null, domain: null })
  },

  /**
   * Refresca la sesión contra el servidor: usuario, permisos **y empresas**.
   *
   * La lista de empresas se guardaba sólo al iniciar sesión, así que una empresa
   * retirada seguía apareciendo en el conmutador hasta volver a entrar. Ahora se
   * rehace en cada arranque del panel; si la empresa activa ya no es suya, se
   * cambia a la que le quede por defecto.
   */
  async me() {
    const { data } = await api.get('/auth/me')
    const user = data.data as Record<string, unknown> & {
      companies?: LoginResponseType['companies']
      companyId?: string | null
    }
    setAuthStore('user', user)

    if (Array.isArray(user.companies)) {
      setAuthStore('companies', user.companies)

      const activo = user.companyId ?? null
      const vigente = user.companies.find(c => c.company.id === activo) ?? user.companies.find(c => c.isDefault) ?? user.companies[0]

      if (vigente && vigente.company.id !== authStore.companyId) {
        // Cambió la empresa activa: hace falta un token nuevo, el anterior
        // apunta a una empresa que ya no le corresponde.
        await authService.selectCompany(vigente.company.id)
      } else if (vigente) {
        setCompanyStore({
          id: vigente.company.id,
          name: vigente.company.name,
          slug: vigente.company.slug,
          logo: vigente.company.logo ?? null,
          domain: vigente.company.domain ?? null
        })
      } else {
        // Sin empresa asignada (cuenta pendiente): limpiar el store persistido
        // para que el panel no muestre una empresa que ya no es suya.
        setAuthStore('companyId', null)
        setCompanyStore({ id: null, name: null, slug: null, logo: null, domain: null })
      }
    }

    return user
  },

  /** Actualiza el propio perfil y los datos de facturación/banco del usuario. */
  async updateMe(payload: { name?: string; document?: string; billingInfo?: Record<string, unknown> }) {
    const { data } = await api.put('/auth/me', payload)
    const user = data.data
    setAuthStore('user', user)
    return user
  },

  async selectCompany(companyId: string) {
    const { data } = await api.post('/auth/select-company', { companyId })
    const result = data.data as { token: string; companyId: string }

    setAuthStore('token', result.token)
    setAuthStore('companyId', result.companyId)

    const company = authStore.companies.find(c => c.company.id === result.companyId)?.company
    if (company) {
      setCompanyStore({
        id: company.id,
        name: company.name,
        slug: company.slug,
        logo: company.logo ?? null,
        domain: company.domain ?? null
      })
    }

    return result
  },

  async recover(email: string, password: string) {
    const { data } = await api.post('/auth/recover', { email, password })
    return data.data
  }
}
